import { formatDate, formatFloat } from '../../../../helpers';

// The single source of truth for "does this document reach the accountant?".
//
// Before Tier 2 the answer lived in three places: the tabs showed every row, the
// Excel button re-derived its own filters, and the IVA tiles used a third rule.
// The tabs remain a simple full review list. The workbook, IVA tiles, Resumen and
// pendientes counts all ask the predicates below so their accounting results
// cannot drift even though export eligibility is no longer marked on screen.

// ── Row models ───────────────────────────────────────────────────────────────
// Every row carries RAW values: numbers stay numbers, dates stay their stored
// string, and a transfer list stays a list. Formatting happens at the point of
// display (`accounting-tabs.tsx`), of export (`accounting-excel-export.ts`) and
// of clipboard output (below) — never in the model. `null` on a numeric field
// means "no value for this row" (rendered blank), which is not a real 0.

export type TransferDetail = {
    accountAbbreviation: string;
    date: string | null;
    amount: number;
};

export type LedgerRow = {
    transferIndex: number;
    transferTypeId: number;
    transferType: 'Cargo' | 'Abono' | 'Traspaso entre cuentas';
    fromAccountName: string;
    toAccountName: string;
    transferDate: string | null;
    orderCode: number | string;
    receiptIssueDate: string | null;
    transferredAmount: number | null;
    receiptTotal: number | null;
    receiptTax: number | null;
    taxRetained: number | null;
    nonTaxRetained: number | null;
    notes: string;
    supplement: string;
    creditNote: string;
    // A canceled document should never keep a transfer attached — that rule is
    // not enforced at capture time yet, so when it happens the row stays (the
    // money did move and the bank statement shows it) and is flagged instead.
    documentCanceled: boolean;
    missingFolio: boolean;
    missingSupplement: boolean;
    missingCreditNote: boolean;
    missingTax: boolean;
    orderSaleId: number | null;
    expenseId: number | null;
    documentIdentifier: string;
    documentAccountName: string;
    documentTransferAmount: number;
};

export type AccountPage = {
    accountId: number;
    accountName: string;
    excludeFromAccountabilityExport: boolean;
    data: LedgerRow[];
};

export type OrderSaleRow = {
    id: number;
    orderCode: number;
    requiresInvoiceCode: boolean;
    invoiceCode: string;
    date: string | null;
    accountName: string;
    total: number;
    tax: number;
    transferReceiptsTotal: number;
    transfers: TransferDetail[];
    supplement: string;
    creditNote: string;
    notes: string;
    includeInAccountabilityExport: boolean;
    // Missing-paperwork flags, decided against the raw document fields at build
    // time so no reader has to re-parse a display string like 'PENDIENTE'.
    missingInvoiceCode: boolean;
    missingSupplement: boolean;
    missingCreditNote: boolean;
    missingTax: boolean;
};

export type ExpenseRow = {
    id: number;
    requiresExternalCode: boolean;
    orderCode: string;
    date: string | null;
    excludeFromAccountabilityExport: boolean;
    includeInAccountabilityExport: boolean;
    paidWithNotasAccount: boolean;
    accountName: string;
    tax: number;
    taxRetained: number;
    nonTaxRetained: number;
    totalWithTax: number;
    transferReceiptsTotal: number;
    transfers: TransferDetail[];
    supplement: string;
    notes: string;
    missingExternalCode: boolean;
    missingSupplement: boolean;
    missingTax: boolean;
};

export type AccountingWorkSheets = {
    accountsPages: AccountPage[];
    orderSalePage: OrderSaleRow[];
    canceledOrderSalePage: OrderSaleRow[];
    expensePage: ExpenseRow[];
    canceledExpensePage: ExpenseRow[];
};

// ── Export eligibility ───────────────────────────────────────────────────────

export type ExportExclusionReason =
    | 'RECEIPT_TYPE'
    | 'EXCLUDED_ACCOUNT'
    | 'PAID_WITH_EXCLUDED_ACCOUNT';

/** `null` means the sale reaches the accountant. */
export function orderSaleExclusion(
    row: Pick<OrderSaleRow, 'includeInAccountabilityExport'>,
): ExportExclusionReason | null {
    return row.includeInAccountabilityExport ? null : 'RECEIPT_TYPE';
}

/** `null` means the expense reaches the accountant. */
export function expenseExclusion(
    row: Pick<
        ExpenseRow,
        | 'includeInAccountabilityExport'
        | 'excludeFromAccountabilityExport'
        | 'paidWithNotasAccount'
    >,
): ExportExclusionReason | null {
    if (row.excludeFromAccountabilityExport) return 'EXCLUDED_ACCOUNT';
    if (row.paidWithNotasAccount) return 'PAID_WITH_EXCLUDED_ACCOUNT';
    if (!row.includeInAccountabilityExport) return 'RECEIPT_TYPE';
    return null;
}

/**
 * The worksheets reduced to what the accountant actually receives. The workbook
 * and the pendientes panel both read this — never the raw pages.
 *
 * Bank-account pages keep every row they have, including type-1 traspasos
 * between own accounts and rows whose document is canceled: those worksheets
 * mirror the bank's estado de cuenta, which shows them. Only the excluded
 * account itself drops out — there is no row-level predicate here on purpose.
 *
 * A canceled document therefore reaches the bank totals, unlike anywhere else.
 * That is not an oversight: a canceled document should never be on a bank
 * worksheet at all, so one that is means a transfer is still attached to it and
 * belongs on a document that is not canceled. The resulting disagreement with
 * the Resumen's IVA is the signal that the correction is pending. Do not add a
 * canceled filter here — it would hide exactly what the `⚠` flag exists to show.
 */
export function buildAccountingExport(
    worksheets: AccountingWorkSheets,
): AccountingWorkSheets {
    return {
        accountsPages: worksheets.accountsPages.filter(
            (page) => !page.excludeFromAccountabilityExport,
        ),
        orderSalePage: worksheets.orderSalePage.filter(
            (row) => orderSaleExclusion(row) === null,
        ),
        canceledOrderSalePage: worksheets.canceledOrderSalePage.filter(
            (row) => orderSaleExclusion(row) === null,
        ),
        expensePage: worksheets.expensePage.filter(
            (row) => expenseExclusion(row) === null,
        ),
        canceledExpensePage: worksheets.canceledExpensePage.filter(
            (row) => expenseExclusion(row) === null,
        ),
    };
}

// ── Missing requirements ─────────────────────────────────────────────────────
// Canceled documents are never checked: they do not require follow-up, and they
// only exist on their own worksheet. Callers pass the ACTIVE export-eligible
// pages.

export type MissingRequirement =
    | 'FOLIO'
    | 'SUPPLEMENT'
    | 'CREDIT_NOTE'
    | 'TAX';

// The missing-invoice requirement reads "Falta factura" everywhere it surfaces —
// on-screen alert columns and pending chips as well as the clipboard message and
// the Excel export. It names what is actually wrong: the transfer was made but we
// still lack the invoice (their internal code, our external code) that legally
// supports it. Only the folio *value* columns keep the "Folio" header.
export const MISSING_REQUIREMENT_LABEL: Record<MissingRequirement, string> = {
    FOLIO: 'Falta factura',
    SUPPLEMENT: 'Complemento',
    CREDIT_NOTE: 'Nota de crédito',
    TAX: 'IVA',
};

export type PendingDocument = {
    // The folio, when the document has one worth naming. Blank when it does not:
    // a placeholder like `#0` reads as a real identifier and sends the reader
    // looking for a document that does not exist. The account, amount and
    // transfers below still identify the row.
    identifier: string;
    accountName: string;
    totalWithTax: number;
    transfers: TransferDetail[];
    missing: MissingRequirement[];
};

export type PendingCounts = {
    /** Documents missing at least one item. A document missing two counts once. */
    documents: number;
    /** …and once under each item it is missing. */
    byRequirement: Record<MissingRequirement, number>;
};

function orderSaleMissing(row: OrderSaleRow): MissingRequirement[] {
    const missing: MissingRequirement[] = [];
    if (row.missingInvoiceCode) missing.push('FOLIO');
    if (row.missingSupplement) missing.push('SUPPLEMENT');
    if (row.missingCreditNote) missing.push('CREDIT_NOTE');
    if (row.missingTax) missing.push('TAX');
    return missing;
}

function expenseMissing(row: ExpenseRow): MissingRequirement[] {
    const missing: MissingRequirement[] = [];
    if (row.missingExternalCode) missing.push('FOLIO');
    if (row.missingSupplement) missing.push('SUPPLEMENT');
    if (row.missingTax) missing.push('TAX');
    return missing;
}

function ledgerMissing(row: LedgerRow): MissingRequirement[] {
    const missing: MissingRequirement[] = [];
    if (row.missingFolio) missing.push('FOLIO');
    if (row.missingSupplement) missing.push('SUPPLEMENT');
    if (row.missingCreditNote) missing.push('CREDIT_NOTE');
    if (row.missingTax) missing.push('TAX');
    return missing;
}

/**
 * Documents attached to a bank transfer that are still missing paperwork, split
 * by the kind of document behind the transfer. These are the highest-priority
 * pendientes: the money already moved, so the paperwork is overdue rather than
 * merely expected. A sale or expense paid from several accounts appears once.
 * Every linked transfer allocation is retained on that one document.
 */
export function pendingLedgerRows(pages: AccountPage[]): {
    sales: PendingDocument[];
    expenses: PendingDocument[];
} {
    const pendingByDocument = new Map<
        string,
        {
            document: PendingDocument;
            isSale: boolean;
            transferKeys: Set<string>;
        }
    >();
    for (const page of pages) {
        for (const row of page.data) {
            if (row.documentCanceled) continue;
            const missing = ledgerMissing(row);
            if (missing.length === 0) continue;
            const isSale = row.orderSaleId != null;
            const key = isSale
                ? `sale:${row.orderSaleId}`
                : row.expenseId != null
                ? `expense:${row.expenseId}`
                : null;
            if (key == null) continue;
            let pending = pendingByDocument.get(key);
            if (!pending) {
                pending = {
                    document: {
                        identifier: row.documentIdentifier,
                        accountName: row.documentAccountName,
                        totalWithTax: row.receiptTotal ?? 0,
                        transfers: [],
                        missing,
                    },
                    isSale,
                    transferKeys: new Set<string>(),
                };
                pendingByDocument.set(key, pending);
            }
            const transferKey = `${page.accountId}:${row.transferIndex}`;
            if (!pending.transferKeys.has(transferKey)) {
                pending.transferKeys.add(transferKey);
                pending.document.transfers.push({
                    accountAbbreviation: '',
                    date: row.transferDate,
                    amount: row.documentTransferAmount,
                });
            }
        }
    }
    const pending = Array.from(pendingByDocument.values());
    return {
        sales: pending
            .filter((entry) => entry.isSale)
            .map((entry) => entry.document),
        expenses: pending
            .filter((entry) => !entry.isSale)
            .map((entry) => entry.document),
    };
}

export function pendingOrderSales(rows: OrderSaleRow[]): PendingDocument[] {
    return rows
        .map((row) => ({
            identifier: String(row.orderCode),
            accountName: row.accountName,
            totalWithTax: row.total,
            transfers: row.transfers,
            missing: orderSaleMissing(row),
        }))
        .filter((doc) => doc.missing.length > 0);
}

export function pendingExpenses(rows: ExpenseRow[]): PendingDocument[] {
    return rows
        .map((row) => ({
            // Only a folio the supplier actually owes us AND that was captured
            // is worth printing. Expenses carry no other usable public number:
            // `internal_code` is unset on ~95% of rows.
            identifier:
                row.requiresExternalCode && row.orderCode !== ''
                    ? row.orderCode
                    : '',
            accountName: row.accountName,
            totalWithTax: row.totalWithTax,
            transfers: row.transfers,
            missing: expenseMissing(row),
        }))
        .filter((doc) => doc.missing.length > 0);
}

export function pendingCounts(documents: PendingDocument[]): PendingCounts {
    const byRequirement: Record<MissingRequirement, number> = {
        FOLIO: 0,
        SUPPLEMENT: 0,
        CREDIT_NOTE: 0,
        TAX: 0,
    };
    documents.forEach((doc) => {
        doc.missing.forEach((requirement) => {
            byRequirement[requirement] += 1;
        });
    });
    return { documents: documents.length, byRequirement };
}

// ── Display / output formatting ──────────────────────────────────────────────

/** The transfer list as one cell of text: `BAN 2026-07-14 (5,000.00), ...`. */
export function formatTransferList(transfers: TransferDetail[]): string {
    return transfers
        .map(
            (transfer) =>
                `${transfer.accountAbbreviation} ${formatDate(
                    transfer.date,
                )} (${formatFloat(transfer.amount)})`,
        )
        .join(', ');
}

/**
 * One line per pending document, ready to paste into a message. A document with
 * no transfer says so explicitly rather than showing an empty field — the
 * missing payment is itself part of what the reader has to chase.
 */
export function pendingClipboardText(
    kindLabel: string,
    periodLabel: string,
    documents: PendingDocument[],
): string {
    const header = `Pendientes de ${kindLabel} — ${periodLabel} (${documents.length})`;
    const lines = documents.map((doc) => {
        const missing = doc.missing
            .map((requirement) => MISSING_REQUIREMENT_LABEL[requirement])
            .join(', ');
        const transfers =
            doc.transfers.length > 0
                ? `Transferencias: ${doc.transfers
                      .map(
                          (transfer) =>
                              `${formatDate(transfer.date)} - $${formatFloat(
                                  transfer.amount,
                              )}`,
                      )
                      .join(', ')}`
                : 'Transferencia: Sin registrar';
        // A document with no folio simply starts at its account — no empty
        // leading segment.
        return [
            doc.identifier === ''
                ? null
                : `Documento: ${doc.identifier}`,
            `Cuenta: ${doc.accountName}`,
            `Pendiente: ${missing}`,
            transfers,
            `Total con IVA: $${formatFloat(doc.totalWithTax)}`,
        ]
            .filter((segment): segment is string => segment !== null)
            .join(' | ');
    });
    return [header, ...lines].join('\n');
}
